import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ExternalLink, Mail, X } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { ApiItem, toStatus } from "../utils/applicationCards";
import { OUTLINE_TAG, STATUS_TAG, TAG, formatAppDate } from "./applicationShared";
import { cn } from "../lib/utils";
import PrepOaRow from "./prep/PrepOaRow";

// Side panel for one application: every email Gmail sync linked to it,
// newest first, with a link to the thread in Gmail.

type EmailEntry = {
  id: string;
  subject: string;
  body: string;
  date: string;
  label: string;
  sender: string;
  kind?: "oa_completed";
};

// Sync stores "Subject\n\nBody" as one string.
function splitEmail(text: string) {
  const [first, ...rest] = text.split("\n");
  return {
    subject: first.trim(),
    body: rest.join("\n").replace(/\n{3,}/g, "\n\n").trim(),
  };
}

function gmailThreadUrl(threadId: string) {
  // authuser picks the right Gmail when the user has several accounts open
  const account = auth.currentUser?.email;
  return `https://mail.google.com/mail/${account ? `?authuser=${encodeURIComponent(account)}` : "u/0/"}#all/${threadId}`;
}

async function loadEmails(item: ApiItem): Promise<EmailEntry[]> {
  const user = auth.currentUser;
  // The card's `emails` list (newer syncs) has each email's label and date
  const cardEmails = new Map((item.emails || []).map((e) => [e.message_id, e]));
  const ids = Array.from(new Set([...(item.messageIds || []), ...Array.from(cardEmails.keys())]));
  if (!user || ids.length === 0) return [];

  const docs = await Promise.all(
    ids.map((id) => getDoc(doc(db, "users", user.uid, "processed_emails", id)).catch(() => null))
  );
  const entries: EmailEntry[] = [];
  docs.forEach((snap, i) => {
    const data = snap?.exists() ? snap.data() : null;
    // The card itself keeps the first email's text, in case its record is missing
    const text = typeof data?.body === "string" ? data.body : i === 0 ? item.body || "" : "";
    const meta = cardEmails.get(ids[i]);
    if (!text && !meta) return;
    const parts = splitEmail(text);
    entries.push({
      id: ids[i],
      subject: data?.subject || meta?.subject || parts.subject,
      body: parts.body,
      // Prefer the card's ISO timestamp; processed_emails keeps Gmail's header date
      date: meta?.date || data?.date || (i === 0 ? item.date : ""),
      label: meta?.label || data?.label || "",
      sender: data?.sender_name || data?.sender_email || "",
      ...(meta?.kind ? { kind: meta.kind } : {}),
    });
  });
  return entries.sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0));
}

function EmailItem({ email }: { email: EmailEntry }) {
  const [expanded, setExpanded] = useState(false);
  const status = toStatus(email.label);
  const long = email.body.length > 420;

  return (
    <article className="rounded-[14px] border border-melt-text1/[.12] bg-melt-paper p-4">
      <div className="flex items-center gap-2">
        {email.kind === "oa_completed" ? (
          <span className={OUTLINE_TAG}>OA done</span>
        ) : status ? (
          <span className={cn(TAG, STATUS_TAG[status].className)}>{STATUS_TAG[status].label}</span>
        ) : email.label ? (
          <span className={OUTLINE_TAG}>{email.label}</span>
        ) : null}
        <span className="ml-auto whitespace-nowrap font-plex text-[11px] text-melt-text3">
          {formatAppDate(email.date)}
        </span>
      </div>
      {email.subject && <h3 className="mt-2.5 text-sm font-semibold leading-snug text-melt-text1">{email.subject}</h3>}
      {email.sender && <p className="mt-0.5 text-[12.5px] text-melt-text3">From {email.sender}</p>}
      {email.body && (
        <p
          className={cn(
            "mt-1.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-melt-text2",
            !expanded && long && "line-clamp-[8]"
          )}
        >
          {email.body}
        </p>
      )}
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-[12.5px] font-medium text-melt-caramel hover:text-melt-press"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </article>
  );
}

export default function ApplicationEmailsSheet({ item: openItem, onClose }: { item: ApiItem | null; onClose: () => void }) {
  // Keep showing the last application while the panel slides out
  const [item, setItem] = useState<ApiItem | null>(openItem);
  useEffect(() => {
    if (openItem) setItem(openItem);
  }, [openItem]);

  const [emails, setEmails] = useState<EmailEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setEmails(null);
    setError(false);
    loadEmails(item)
      .then((list) => !cancelled && setEmails(list))
      .catch((e) => {
        console.error("Could not load emails:", e);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item]);

  const status = item ? toStatus(item.predicted_label) ?? "submitted" : null;
  const threadId = item?.threadIds?.[item.threadIds.length - 1];

  return (
    <DialogPrimitive.Root open={Boolean(openItem)} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[460px] flex-col border-l border-melt-hair bg-[#FDFBF8] font-grotesk text-melt-text1 shadow-[-24px_0_48px_-24px_rgba(92,52,23,.35)] outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        >
          {item && status && (
            <>
              <header className="border-b border-melt-hair px-6 pb-5 pt-6">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <DialogPrimitive.Title className="truncate text-[22px] font-semibold tracking-[-.02em]">
                      {item.company || "Unknown"}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="mt-0.5 line-clamp-2 text-[13.5px] [overflow-wrap:anywhere] text-melt-text2">
                      {item.role || "Role not detected"}
                    </DialogPrimitive.Description>
                  </div>
                  <DialogPrimitive.Close
                    aria-label="Close"
                    className="-mr-2 grid h-8 w-8 flex-none place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
                  >
                    <X size={18} strokeWidth={1.75} />
                  </DialogPrimitive.Close>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className={cn(TAG, STATUS_TAG[status].className)}>{STATUS_TAG[status].label}</span>
                  {item.szn && <span className={OUTLINE_TAG}>{item.szn}</span>}
                  {item.lctn && <span className={OUTLINE_TAG}>{item.lctn}</span>}
                  {threadId && (
                    <a
                      href={gmailThreadUrl(threadId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.05] hover:text-melt-text1"
                    >
                      Open in Gmail
                      <ExternalLink size={13} strokeWidth={1.75} />
                    </a>
                  )}
                </div>
                <PrepOaRow card={item} />
              </header>

              <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-5">
                <div className="mb-3 flex items-center gap-2 font-plex text-[10.5px] font-medium uppercase tracking-[.1em] text-melt-text3">
                  Emails{emails && emails.length > 0 && <span>· {emails.length}</span>}
                </div>

                {error ? (
                  <p className="text-sm text-[#9A4F33]">Couldn’t load the emails for this application.</p>
                ) : emails === null ? (
                  <div className="space-y-3">
                    {[0, 1].map((i) => (
                      <div key={i} className="h-28 animate-pulse rounded-[14px] bg-melt-text1/[.05]" />
                    ))}
                  </div>
                ) : emails.length === 0 ? (
                  <div className="flex flex-col items-center rounded-[14px] border border-melt-line px-6 py-8 text-center">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-melt-peach text-melt-amber">
                      <Mail size={18} strokeWidth={1.75} />
                    </span>
                    <p className="mt-3 text-sm font-semibold">
                      {item.manual ? "Added manually" : "No emails found"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {emails.map((email) => (
                      <EmailItem key={email.id} email={email} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
